import { useTranslation } from "react-i18next";
import { Icon } from "@core/assets";
import type { PublicEligibility } from "../../types";

/** One "this call is for…" line. A bullet, never a checkbox — nothing here is a test to pass. */
function CriterionRow({ children }: { children: string }) {
  return (
    <li className="flex items-start gap-2.5">
      <span
        aria-hidden
        className="mt-2 size-1.5 shrink-0 rounded-full bg-(--text-tertiary)"
      />
      <span className="min-w-0 flex-1 wrap-break-word text-sm-regular text-(--text-primary)">
        {children}
      </span>
    </li>
  );
}

/**
 * « Ce que ça vous coûtera » — US-E11-01 step 4: what applying costs in time, and who the call is for.
 *
 * ## The copy here is load-bearing, and it is the story
 *
 * US-E3-04 and US-E11-02 both say the same thing in different words: **a criterion that reads as a
 * verdict becomes a self-rejection**, and it turns away exactly the rural, less-credentialled
 * applicants the foundation names as a priority. `failed_gates_behavior` is hard-coded
 * `human_review_only` server-side — failing any of these never rejects anyone. So the list is
 * phrased as an address ("this call is for…"), never as a rule, and the closing note says outright
 * that not matching one does not stop you applying.
 *
 * **Nothing here may become a client-side gate.** No disabled state, no filtering, no warning
 * styling. The list is information.
 */
export function WhatItCosts({
  eligibility,
}: {
  eligibility: PublicEligibility;
}) {
  const { t } = useTranslation("acquire");

  /**
   * Only the criteria the round actually publishes.
   *
   * The design also draws a fifth bullet about registered ventures — that preference exists in the
   * product (Q6) but is NOT in the public eligibility payload, so it is omitted rather than
   * hardcoded. A criterion written into this page could not follow the round that changed it, and
   * an applicant reading a stale condition is the failure US-W0-03 exists to prevent.
   */
  const criteria = [
    t("preview.cost.criterion-age", {
      min: eligibility.age_min,
      max: eligibility.age_max,
    }),
    eligibility.residency_required
      ? // The round may supply its own wording for what residency means; prefer it over ours.
        eligibility.residency_description ||
        t("preview.cost.criterion-residency")
      : null,
    eligibility.wilaya_mode === "SELECTED" &&
    eligibility.wilaya_codes.length > 0
      ? t("preview.cost.criterion-wilayas", {
          count: eligibility.wilaya_codes.length,
        })
      : null,
    // Blank on a round that sets no minimum — `allow_blank`, so guard on the label not the code.
    eligibility.education_minimum
      ? t("preview.cost.criterion-education", {
          level: eligibility.education_minimum,
        })
      : null,
  ].filter((line): line is string => Boolean(line));

  return (
    <section className="flex flex-col gap-lg border-y border-(--border-default) bg-surface-card px-md pt-lg pb-xl sm:px-xl">
      <div className="flex flex-col gap-xxs">
        <h2 className="text-md-semibold text-(--text-primary)">
          {t("preview.cost.title")}
        </h2>
        <p className="text-sm-regular text-(--text-secondary)">
          {t("preview.cost.subtitle")}
        </p>
      </div>

      {/*
        ⚠️ PROVISIONAL COPY. US-E11-01 step 4 says the time estimate should be "based on what
        candidats actually took, once there is data to base it on" — and there is none before the
        pilot. It lives in the locale file so it can be corrected without a deploy, and it is
        deliberately a range rather than a number.
      */}
      <div className="flex items-start gap-2.5">
        <span aria-hidden className="mt-0.5 shrink-0 text-(--text-tertiary)">
          <Icon name="clock" size={18} />
        </span>
        <div className="min-w-0 flex-1">
          <p className="text-sm-semibold text-(--text-primary)">
            {t("preview.cost.time-estimate")}
          </p>
          <p className="text-sm-regular text-(--text-secondary)">
            {t("preview.cost.time-note")}
          </p>
        </div>
      </div>

      {criteria.length > 0 ? (
        <div className="flex flex-col gap-1.5">
          <h3 className="text-xs-semibold tracking-wide text-(--text-tertiary) uppercase">
            {t("preview.cost.audience-heading")}
          </h3>
          <ul className="flex flex-col gap-1.5">
            {criteria.map((line) => (
              <CriterionRow key={line}>{line}</CriterionRow>
            ))}
          </ul>
        </div>
      ) : null}

      {/*
        The sentence that keeps the door open. Info-toned, never a warning: an alert glyph on this
        note would turn a reassurance into the verdict it exists to prevent.
      */}
      <p className="flex items-start gap-2.5 rounded-lg bg-surface-page px-sm py-2.5 text-sm-regular text-(--text-primary)">
        <span
          aria-hidden
          className="mt-0.5 shrink-0 text-(--feedback-info-icon)"
        >
          <Icon name="info-circle" size={16} />
        </span>
        {t("preview.cost.never-blocks")}
      </p>
    </section>
  );
}
